import { useState, useCallback, useEffect } from 'react';

import { debounce } from 'lodash';

import {
  SUMMATIVETEST_MAX_SUBTOPICS,
  SUMMATIVETEST_MAX_TOPICS,
} from '@lessonPlanner/constants';

export default function useTopics({
  topics = [],
  setValue,
  triggerValidation,
  FORM_FIELDS,
}) {
  const [showSubtopics, setShowSubtopics] = useState<boolean[]>([]);

  // Ensure showSubtopics is synchronized with topics array
  useEffect(() => {
    if (Array.isArray(topics)) {
      const subtopicVisibility = topics.map((topic) => {
        return topic.subtopics && topic.subtopics.length > 0 &&
               topic.subtopics.some(subtopic => subtopic.trim() !== '');
      });
      setShowSubtopics(subtopicVisibility);
    } else {
      setShowSubtopics([false]);
    }
  }, [topics.length]);

  const [emptySubtopicsIndex, setEmptySubtopics] = useState<{
    topicIndex: number | null;
    subtopicIndex: number[];
  }>({
    topicIndex: null,
    subtopicIndex: [],
  });

  const toggleSubtopicsVisibility = (topicIndex: number) => {
    const hasNoSubtopics = !topics[topicIndex]?.subtopics?.length;

    if (!showSubtopics[topicIndex] && hasNoSubtopics) {
      const newTopics = [...topics];
      newTopics[topicIndex].subtopics = [''];
      setValue('topics', newTopics);
    }

    setShowSubtopics((prevShowSubtopics) => {
      const newShowSubtopics = [...prevShowSubtopics];
      newShowSubtopics[topicIndex] = !newShowSubtopics[topicIndex];
      return newShowSubtopics;
    });
  };

  const addSubtopic = (topicIndex: number) => {
    const emptySubtopics = [];

    topics[topicIndex].subtopics.forEach((subtopic, index) => {
      if (subtopic === '') {
        emptySubtopics.push(index);
      }
    });

    if (emptySubtopics.length > 0) {
      setEmptySubtopics({
        topicIndex,
        subtopicIndex: emptySubtopics,
      });
      return;
    }

    if (topics[topicIndex].subtopics.length < SUMMATIVETEST_MAX_SUBTOPICS) {
      const newTopics = [...topics];
      newTopics[topicIndex].subtopics.push('');
      setValue('topics', newTopics);
      setEmptySubtopics({
        topicIndex: null,
        subtopicIndex: [],
      });
    }
  };

  const addTopic = () => {
    if (topics.length < SUMMATIVETEST_MAX_TOPICS) {
      const newTopics = [...topics, { topic: '', subtopics: [''] }];
      setValue('topics', newTopics);

      setShowSubtopics((prevState) => {
        const newState = [...prevState, false];
        return newState;
      });
    }
  };

  const removeSubtopic = (topicIndex: number, subtopicIndex: number) => {
    const newTopics = [...topics];

    if (newTopics[topicIndex].subtopics.length === 1) {
      setShowSubtopics((prevState) => {
        const newState = [...prevState];
        newState[topicIndex] = false;
        return newState;
      });
      newTopics[topicIndex].subtopics = [''];
    } else {
      newTopics[topicIndex].subtopics.splice(subtopicIndex, 1);
    }

    setValue('topics', newTopics);
    triggerValidation();
  };

  const removeTopic = (topicIndex: number) => {
    const newTopics = [...topics];
    newTopics.splice(topicIndex, 1);
    setValue('topics', newTopics);

    setShowSubtopics((prevState) => {
      const newState = [...prevState];
      newState.splice(topicIndex, 1);
      return newState;
    });

    triggerValidation();
  };

  const handleSubtopicChange = (
    topicIndex: number,
    subtopicIndex: number,
    value: string,
  ) => {
    const newTopics = [...topics];
    newTopics[topicIndex].subtopics[subtopicIndex] = value;

    if (emptySubtopicsIndex.topicIndex === topicIndex) {
      setEmptySubtopics((prev) => {
        return {
          ...prev,
          subtopicIndex: prev.subtopicIndex.filter(
            (index) => index !== subtopicIndex,
          ),
        };
      });
    }

    setValue('topics', newTopics);
  };

  const handleTopicChange = (topicIndex: number, value: string) => {
    const newTopics = [...topics];
    newTopics[topicIndex].topic = value;
    setValue('topics', newTopics);
  };

  async function triggerGradualValidation(fieldName) {
    if (!FORM_FIELDS) return;

    const fieldIndex = FORM_FIELDS.indexOf(fieldName);

    if (fieldIndex === -1) {
      return;
    }

    if (fieldIndex === FORM_FIELDS.length - 1) {
      await triggerValidation();
    } else {
      const fieldsToValidate = FORM_FIELDS.slice(0, fieldIndex + 1);
      await triggerValidation(fieldsToValidate);
    }
  }

  const debouncedValidation = useCallback(
    debounce((field) => {
      triggerGradualValidation(field);
    }, 300),
    [triggerValidation],
  );

  const handleTrimUpdate = (
    index: number,
    value: string,
    type: 'topic' | 'subtopic',
    subIndex?: number,
  ) => {
    const newTopics = [...topics];
    const trimmedValue = value?.trim() || '';

    if (type === 'topic') {
      newTopics[index].topic = trimmedValue;
      setValue('topics', newTopics);
      triggerGradualValidation('topics');
    } else {
      newTopics[index].subtopics[subIndex] = trimmedValue;
      setValue('topics', newTopics);
      triggerValidation(`topics[${index}].subtopics[${subIndex}]`);
    }
  };

  const isTopicEmpty =
    topics?.length > 0 && topics[topics.length - 1]?.topic === '';

  return {
    showSubtopics,
    emptySubtopicsIndex,
    toggleSubtopicsVisibility,
    addSubtopic,
    addTopic,
    removeSubtopic,
    removeTopic,
    handleSubtopicChange,
    handleTopicChange,
    debouncedValidation,
    handleTrimUpdate,
    isTopicEmpty,
    triggerGradualValidation,
  };
}
